import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Client } from "../types";
import { ClientInfo } from "./ClientInfo";
import { Button } from "@/components/ui/button";
import { Calendar, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { DeleteClientDialog } from "./DeleteClientDialog";
import { EditClientDialog } from "./EditClientDialog";
import { ClientExpandedInfo } from "./ClientExpandedInfo";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClientTeams } from "./ClientTeams";
import { ClientGuards } from "./ClientGuards";
import { TeamAssignment } from "./TeamAssignment";
import { cn } from "@/lib/utils";
interface ClientCardProps {
  client: Client;
  onClientRemoved: () => void;
  onClientUpdated: () => void;
  onClientSelected: (client: Client) => void;
  hideActions?: boolean;
}
export const ClientCard = ({
  client,
  onClientRemoved,
  onClientUpdated,
  onClientSelected,
  hideActions = false
}: ClientCardProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isTeamAssignmentOpen, setIsTeamAssignmentOpen] = useState(false);

  // Generate subtle variations based on client ID for visual diversity
  const clientVariation = client.id.charCodeAt(0) % 6;
  const getGradientVariation = () => {
    switch (clientVariation) {
      case 0:
        return "from-blue-50/80 to-blue-100/60 dark:from-blue-950/40 dark:to-blue-900/30";
      // Soft blue
      case 1:
        return "from-purple-50/80 to-purple-100/60 dark:from-purple-950/40 dark:to-purple-900/30";
      // Soft purple
      case 2:
        return "from-emerald-50/80 to-emerald-100/60 dark:from-emerald-950/40 dark:to-emerald-900/30";
      // Soft emerald
      case 3:
        return "from-orange-50/80 to-orange-100/60 dark:from-orange-950/40 dark:to-orange-900/30";
      // Soft orange
      case 4:
        return "from-pink-50/80 to-pink-100/60 dark:from-pink-950/40 dark:to-pink-900/30";
      // Soft pink
      case 5:
        return "from-teal-50/80 to-teal-100/60 dark:from-teal-950/40 dark:to-teal-900/30";
      // Soft teal
      default:
        return "from-blue-50/80 to-blue-100/60 dark:from-blue-950/40 dark:to-blue-900/30";
    }
  };
  const getBorderColor = () => {
    switch (clientVariation) {
      case 0:
        return "border-blue-200/40 dark:border-blue-800/40";
      case 1:
        return "border-purple-200/40 dark:border-purple-800/40";
      case 2:
        return "border-emerald-200/40 dark:border-emerald-800/40";
      case 3:
        return "border-orange-200/40 dark:border-orange-800/40";
      case 4:
        return "border-pink-200/40 dark:border-pink-800/40";
      case 5:
        return "border-teal-200/40 dark:border-teal-800/40";
      default:
        return "border-blue-200/40 dark:border-blue-800/40";
    }
  };
  return <>
      <Card className={cn("relative overflow-hidden group transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg rounded-xl border", `bg-gradient-to-br ${getGradientVariation()}`, getBorderColor())}>
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-8 h-8 bg-white rounded-full -translate-y-4 translate-x-4"></div>
          <div className="absolute bottom-0 left-0 w-6 h-6 bg-white rounded-full translate-y-3 -translate-x-3"></div>
        </div>
        
        {/* Subtle shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700"></div>
        
        <CardHeader className="relative pb-2">
          <div className="flex justify-between items-start">
            <CardTitle className="text-lg font-bold truncate text-slate-700 dark:text-slate-200 group-hover:text-slate-600 dark:group-hover:text-slate-100 transition-colors">
              {client.company_name || `${client.first_name} ${client.last_name}`}
            </CardTitle>
            
            {!hideActions && <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600 dark:text-slate-300 hover:bg-white/40 dark:hover:bg-black/40 backdrop-blur-sm">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setIsEditDialogOpen(true)}>
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setIsTeamAssignmentOpen(true)}>
                    Assign Teams
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setIsDetailsDialogOpen(true)} className="text-primary">
                    View Details
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>}
          </div>
        </CardHeader>
        
        <CardContent className="relative pt-0">
          <ClientInfo client={client} onAssignTeams={() => setIsTeamAssignmentOpen(true)} />
        </CardContent>
        
        <CardFooter className="relative border-t border-white/30 dark:border-white/10 p-3 flex gap-2 justify-end flex-col">
          
          
          {!hideActions && <DeleteClientDialog clientId={client.id} clientName={client.company_name || `${client.first_name} ${client.last_name}`} onClientDeleted={onClientRemoved} trigger={<Button variant="destructive" size="sm" className="w-full gap-2 bg-red-600/90 hover:bg-red-700 backdrop-blur-sm">
                  Delete Client
                </Button>} />}
        </CardFooter>
      </Card>
      
      {/* Dialogs */}
      <EditClientDialog isOpen={isEditDialogOpen} onOpenChange={setIsEditDialogOpen} client={client} onClientUpdated={onClientUpdated} />
      
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <ClientExpandedInfo client={client} />
          
          <Separator className="my-4" />
          
          <ClientTeams clientId={client.id} />
          
          <Separator className="my-4" />
          
          <ClientGuards clientId={client.id} />
        </DialogContent>
      </Dialog>

      {/* Team Assignment Dialog */}
      <Dialog open={isTeamAssignmentOpen} onOpenChange={setIsTeamAssignmentOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign Teams to {client.first_name} {client.last_name}</DialogTitle>
          </DialogHeader>
          <TeamAssignment clientId={client.id} onComplete={() => {
          setIsTeamAssignmentOpen(false);
          onClientUpdated();
        }} />
        </DialogContent>
      </Dialog>
    </>;
};